const getMealTypeGuidelines = (mealType) => {
  switch(mealType.toLowerCase()) {
    case 'breakfast':
      return '- Design for morning energy needs: balanced protein and carbs, moderate healthy fats\n  - Consider prep time for busy mornings\n  - Think about portable options or make-ahead possibilities';
    case 'lunch':
      return '- Focus on sustained energy for afternoon: balanced macros with adequate protein\n  - Consider workplace/school-friendly options\n  - Should be satisfying without causing afternoon energy crash';
    case 'dinner':
      return '- Evening meal focus: can be heartier, emphasis on protein and vegetables\n  - Consider family-friendly portions and flavors\n  - Allow for longer cooking methods if desired';
    case 'snack':
      return '- Small portions, 100-300 calories typically\n  - Focus on protein or healthy fats for satiety\n  - Should be convenient and portable';
    default:
      return '- No specific meal type guidelines';
  }
};

export const RecipeGenerationMessage = ({ macrosString, diet, mealType, customInstructions, recipes }) => {
  // get array of recipe names
  const recipeNames = recipes.map((recipe) => recipe.name).join(", ");

  // Parse macrosString to extract specific targets for better prompt engineering
  const hasSpecificMacros = macrosString && macrosString.trim().length > 0;
  
  // Define allowed units for ingredients
  const allowedUnits = [
    "g", "mg", "kg", "ml", "l", "tsp", "tbsp", "lb", "oz", "floz", 
    "pinch", "dash", "drop", "can", "piece", "slice", "scoop", "fillet", 
    "item", "cup", "pt", "gal", "qt", "clove"
  ];
  
  // Build meal type context
  const mealTypeContext = mealType ? `
  MEAL TYPE: ${mealType.toUpperCase()}
  ${getMealTypeGuidelines(mealType)}` : "";
  
  // Build custom instructions context
  const customInstructionsContext = customInstructions && customInstructions.trim() ? `
  CUSTOM INSTRUCTIONS: "${customInstructions}"
  Strictly follow these specific requirements in addition to all other constraints.` : "";
  
  return [
    {
      role: "system",
      content: `You are a professional chef and registered dietitian with expertise in creating nutritionally precise recipes. You excel at:
      1. Calculating exact macronutrient values for ingredients and recipes
      2. Adapting recipes to meet specific dietary restrictions and allergies
      3. Balancing nutritional goals with culinary excellence
      4. Explaining nutritional trade-offs when perfect targets cannot be achieved
      5. Using ingredient substitutions to optimize both nutrition and taste
      6. Precise nutritional calculations for individual ingredients based on USDA data`,
    },
    {
      role: "user",
      content: `Create ONE complete recipe with the following requirements:

      NUTRITIONAL TARGETS (per serving):
      ${hasSpecificMacros ? macrosString : "No specific macro requirements - optimize for balanced nutrition"}
      
      DIETARY RESTRICTIONS:
      ${diet && diet !== "balanced" ? `STRICT COMPLIANCE required for: ${diet}. Use appropriate substitutions (e.g., plant proteins for vegan, almond flour for gluten-free, etc.)` : "No dietary restrictions"}
      ${mealTypeContext}${customInstructionsContext}
      
      OPTIMIZATION PRIORITY:
      1. FIRST: Follow custom instructions and meal type guidelines
      2. SECOND: Meet all dietary restrictions completely
      3. THIRD: Get as close as possible to macro targets
      4. FOURTH: Ensure recipe is delicious and practical
      
      ${recipeNames ? `AVOID duplicating these existing recipes: ${recipeNames}` : ""}
      
      RESPONSE FORMAT - Respond with ONLY this JSON structure:`,
    },
    {
      role: "user",
      content: `{
  "n": "Creative recipe name that sounds appetizing",
  "d": "Detailed description (200-300 chars) highlighting flavors, texture, and why it's appealing",
  "ns": number_of_servings_as_integer,
  "mealType": "${mealType || "other"}",
  "mac": {
    "p": total_protein_grams_entire_recipe_as_number,
    "c": total_carbs_grams_entire_recipe_as_number, 
    "f": total_fats_grams_entire_recipe_as_number,
    "kCals": total_calories_entire_recipe_as_number,
    "fi": total_dietary_fiber_grams_entire_recipe_as_number
  },
  "ing": [
    {
      "n": "specific ingredient name",
      "q": "precise quantity as number only (e.g., 2, 0.5, 1.25)",
      "u": "unit from allowed list only",
      "category": "shopping_category_from_dairy_meat_seafood_produce_grains_pantry_spices_nuts_oils_beverages_frozen_snacks_other",
      "mac": {
        "p": protein_grams_for_this_ingredient_as_number,
        "c": carbs_grams_for_this_ingredient_as_number,
        "f": fats_grams_for_this_ingredient_as_number,
        "kCals": calories_for_this_ingredient_as_number,
        "fi": dietary_fiber_grams_for_this_ingredient_as_number
      }
    }
  ],
  "i": [
    {
      "step": "Step-by-step cooking instruction with timing and temperatures",
      "duration": "optional_duration_in_seconds_as_number_or_null"
    }
  ],
  "analysis": {
    "macro_accuracy": "percentage_how_close_to_targets",
    "dietary_compliance": "100% or explain any issues", 
    "reasoning": "Brief explanation of ingredient choices and any compromises made to balance targets with dietary restrictions"
  }
}`,
    },
    {
      role: "user",
      content: `CRITICAL CALCULATION REQUIREMENTS:
      
      ALLOWED UNITS ONLY - Use these units for ingredients:
      ${allowedUnits.join(", ")}
      
      SMART UNIT SELECTION GUIDELINES:
      - SOLIDS/DRY INGREDIENTS (quinoa, rice, flour, nuts, seeds, cheese, pasta, etc.): Use WEIGHT units (g, kg, oz, lb) for accuracy and shopping practicality
      - LIQUIDS: Use VOLUME units (ml, l, floz, cup) 
      - SPICES/SMALL AMOUNTS: Use tsp, tbsp, pinch, dash for very small quantities
      - WHOLE ITEMS: Use piece, slice, can, clove for discrete items
      - PREFER: grams (g) for most solid ingredients under 500g, kg for larger amounts
      - AVOID: volume units (cup, tbsp) for solid ingredients that are typically bought by weight
      
      Examples of GOOD unit choices:
      ✓ "quinoa": "150g" (not "1 cup")
      ✓ "chicken breast": "200g" (not "1 piece")  
      ✓ "olive oil": "2 tbsp" or "30ml"
      ✓ "salt": "1 tsp" or "5g"
      ✓ "flour": "120g" (not "1 cup")
      ✓ "rice": "100g" (not "0.5 cup")
      
      INGREDIENT MACRO CALCULATIONS:
      - Each ingredient MUST have individual macros that are calculated based on the exact quantity
      - Sum of all ingredient macros MUST equal the total recipe macros
      - Use USDA nutrition database values for accuracy
      - Show your math: ingredient quantities × nutritional density = individual macros
      
      MACRO TARGET ACCURACY:
      ${
        hasSpecificMacros
          ? `- Aim for within 10% of specified macro ranges per serving
        - If impossible due to dietary restrictions, explain why in "reasoning"
        - Calculate per-serving macros: total recipe macros ÷ number of servings`
          : `- Optimize for balanced nutrition: aim for 20-30% protein, 40-50% carbs, 25-35% fats`
      }
      
      DIETARY RESTRICTION COMPLIANCE:
      ${
        diet && diet !== "balanced"
          ? `- ${diet
              .split(", ")
              .map((restriction) => {
                switch (restriction.toLowerCase()) {
                  case "keto":
                    return "Keep carbs under 20g per serving, high fat content";
                  case "low carb":
                    return "Keep carbs under 50g per serving, moderate protein and fat";
                  case "vegan":
                    return "Zero animal products - no meat, dairy, eggs, honey";
                  case "vegetarian":
                    return "No meat, poultry, or fish - dairy and eggs OK";
                  case "gluten free":
                    return "No wheat, barley, rye, or gluten-containing ingredients";
                  case "dairy free":
                    return "No milk, cheese, butter, yogurt, or dairy derivatives";
                  case "paleo":
                    return "No grains, legumes, dairy, processed foods";
                  case "clean":
                    return "No grains, legumes, dairy, sugar, alcohol, processed foods, or additives";
                  case "mediterranean":
                    return "Emphasize olive oil, fish, vegetables, whole grains";
                  case "atkins":
                    return "Very low carb, high protein and fat";
                  case "high carb":
                    return "Emphasize complex carbohydrates, moderate protein";
                  case "fat free":
                    return "Minimize fats to under 5g per serving";
                  default:
                    return `Follow ${restriction} guidelines strictly`;
                }
              })
              .join("\n        - ")}`
          : "- No specific dietary restrictions to follow"
      }
      
      INGREDIENT PRECISION:
      - Use exact measurements that real people can follow
      - Choose ingredients that work together culinarily  
      - Calculate individual ingredient macros based on USDA nutrition data
      - Assign appropriate shopping categories: dairy, meat, seafood, produce, grains, pantry, spices, nuts, oils, beverages, frozen, snacks, other
      - Verify math: sum of all ingredient macros should equal recipe totals
      - Quantities should be practical (e.g., 250g chicken breast, 2 tbsp olive oil, 1 cup rice)
      
      RECIPE QUALITY:
      - 4-8 ingredients maximum for practicality
      - 4-8 clear, actionable cooking steps with step text and optional duration
      - Include cooking times and temperatures where relevant in the step text
      - Duration should be in seconds for timed steps (e.g., 1200 for 20 min baking, 300 for 5 min cooking, 180 for 3 min resting)
      - Use null for duration if the step doesn't have a specific time requirement
      - Ensure the recipe actually tastes good together
      
      VALIDATION CHECKLIST:
      ✓ All units are from the allowed list
      ✓ All ingredient macros are calculated individually
      ✓ Sum of ingredient macros = total recipe macros
      ✓ Quantities are practical and realistic
      ✓ Recipe meets dietary restrictions
      ✓ Recipe targets specified macro goals
      
      Generate the recipe JSON now:`,
    },
  ];
};

export const IngredientNutritionMessage = ({ ingredientName, unit }) => {
  return [
    {
      role: "system",
      content: `You are a registered dietitian and nutrition data expert with access to comprehensive USDA nutrition databases. Your expertise includes:
      1. Precise macronutrient calculations for ingredients per unit
      2. Understanding standard serving sizes and measurement conversions
      3. Accurate nutritional data based on scientific sources
      4. Converting between different measurement units for consistency
      5. Categorizing ingredients for shopping list organization`,
    },
    {
      role: "user",
      content: `Provide the nutritional information and shopping category for "${ingredientName}" per ${unit}.

INSTRUCTIONS:
- Use USDA nutrition data standards
- Calculate macronutrients per 1 ${unit} of ${ingredientName}
- Values should be in grams only
- Be as accurate as possible with decimal precision
- If the ingredient is highly variable (like "mixed vegetables"), use common averages
- For processed foods, use typical commercial product values
- Choose the most appropriate shopping category from: dairy, meat, seafood, produce, grains, pantry, spices, nuts, oils, beverages, frozen, snacks, other

REQUIRED OUTPUT FORMAT (JSON only, no additional text):
{
  "protein": [protein in grams per ${unit}],
  "carbs": [carbohydrates in grams per ${unit}], 
  "fats": [fats in grams per ${unit}],
  "category": "[shopping category from the list above]"
}

Example for "Chicken Breast" per "100g":
{
  "protein": 23.1,
  "carbs": 0.0,
  "fats": 3.6,
  "category": "meat"
}

Generate the nutrition data and shopping category for "${ingredientName}" per ${unit} now:`,
    },
  ];
};

export const RecipeMacroCalculationMessage = ({ ingredients, servings, recipeName }) => {
  const ingredientsList = ingredients.map(ing => 
    `- ${ing.amount} ${ing.unit} ${ing.name}`
  ).join('\n');

  return [
    {
      role: "system",
      content: `You are a registered dietitian and nutrition expert with access to comprehensive USDA nutrition databases. Your expertise includes:
      1. Precise macronutrient calculations for ingredients based on exact quantities
      2. Accurate nutritional data based on scientific sources
      3. Converting between different measurement units
      4. Calculating per-serving values from total recipe nutrition`
    },
    {
      role: "user", 
      content: `Calculate the total nutritional information for this recipe and provide per-serving values.

RECIPE: ${recipeName || 'Recipe'}
SERVINGS: ${servings}

INGREDIENTS:
${ingredientsList}

INSTRUCTIONS:
- Use USDA nutrition data standards for each ingredient
- Calculate total macros for the entire recipe based on the specific quantities
- Then divide by ${servings} servings to get per-serving values
- Be as accurate as possible with decimal precision
- For processed foods, use typical commercial product values
- If any ingredient is unclear, use common/standard preparation

REQUIRED OUTPUT FORMAT (JSON only, no additional text):
{
  "proteins": [protein in grams per serving],
  "carbs": [carbohydrates in grams per serving],
  "fats": [fats in grams per serving],
  "fiber": [dietary fiber in grams per serving],
  "kCals": [calories per serving]
}

Example output:
{
  "proteins": 25.3,
  "carbs": 12.7,
  "fats": 8.9,
  "fiber": 4.2,
  "kCals": 215
}

Calculate the nutrition data for this recipe now:`
    }
  ];
};

export const MealPlanGenerationMessage = ({ 
  dailyProteins, 
  dailyCarbs, 
  dailyFats, 
  dailyCalories,
  dietaryRestrictions, 
  customInstructions, 
  existingRecipes 
}) => {
  const existingRecipesList = existingRecipes.filter(r => r.matchesRestrictions && !r.isArchived)
    .map(r => `- ${r.name} (${r.mealType}) - P:${r.proteins}g, C:${r.carbs}g, F:${r.fats}g, Fi:${r.fiber ?? 0}g, Cal:${r.kCals}, Servings:${r.servings}`)
    .join('\n');

  return [
    {
      role: "system",
      content: `You are a professional meal planning nutritionist and registered dietitian with expertise in:
      1. Creating balanced meal plans that precisely meet daily macro targets
      2. Incorporating existing recipes when they fit nutritional and dietary requirements
      3. Generating new recipes to fill gaps and provide variety
      4. Ensuring all recipes comply with specific dietary restrictions
      5. Balancing nutritional accuracy with culinary variety and appeal
      6. Creating realistic meal plans that clients will actually follow`
    },
    {
      role: "user",
      content: `Generate a meal plan starter with recipe options for Monday only to begin with.

DAILY MACRO TARGETS (must be met when selecting 1 recipe from each meal type):
- Protein: ${dailyProteins}g
- Carbohydrates: ${dailyCarbs}g  
- Fats: ${dailyFats}g
- Calories: ${dailyCalories} (calculated from macros)

DIETARY RESTRICTIONS: ${dietaryRestrictions}
${customInstructions ? `CUSTOM INSTRUCTIONS: "${customInstructions}"` : ''}

EXISTING RECIPES TO CONSIDER FIRST:
${existingRecipesList || 'No existing recipes match criteria'}

REQUIREMENTS:
1. For each meal type (breakfast, lunch, dinner, snack), provide exactly 1 recipe option for Monday
2. PRIORITIZE using existing recipes that match dietary restrictions and help meet macro targets
3. Generate new recipes only when:
   - No existing recipes for the meal type
   - Existing recipes don't fit macro targets well
   - Existing recipes don't meet dietary restrictions
4. Monday's combination (1 breakfast + 1 lunch + 1 dinner + 1 snack) should total close to macro targets
5. All recipes must strictly comply with dietary restrictions
6. This is a starting foundation - users can add more recipes to other days and meal slots later
7. Include complete ingredient lists with macro breakdowns for new recipes
8. For existing recipes, use the exact data provided
9. Focus on practical recipes that can be prepared easily

MACRO DISTRIBUTION GUIDELINES:
- Breakfast: 20-25% of daily calories
- Lunch: 30-35% of daily calories  
- Dinner: 30-35% of daily calories
- Snack: 10-15% of daily calories`
    },
    {
      role: "user",
      content: `Respond with this exact JSON structure for Monday only. IMPORTANT: Return only raw JSON without any markdown formatting, code blocks, or additional text:

{
  "mealPlan": {
    "breakfast": [
      {
        "n": "Recipe name",
        "d": "Brief appetizing description (100-150 chars)",
        "ns": servings_as_number,
        "mac": {
          "p": total_protein_grams_for_entire_recipe,
          "c": total_carbs_grams_for_entire_recipe,
          "f": total_fats_grams_for_entire_recipe,
          "kCals": total_calories_for_entire_recipe
        },
        "ing": [
          {
            "n": "ingredient name",
            "q": quantity_as_number,
            "u": "unit",
            "mac": {
              "p": protein_grams_for_this_ingredient,
              "c": carbs_grams_for_this_ingredient,
              "f": fats_grams_for_this_ingredient,
              "kCals": calories_for_this_ingredient
            }
          }
        ],
        "i": [
          {
            "step": "cooking instruction",
            "duration": duration_in_seconds_or_null
          }
        ],
        "mealType": "breakfast",
        "isExisting": true_if_from_existing_recipes_false_if_new,
        "existingId": "recipe_id_if_existing_null_if_new",
        "dietaryRestrictions": ["array", "of", "applicable", "restrictions"]
      }
    ],
    "lunch": [
      // 1 lunch recipe with same structure
    ],
    "dinner": [
      // 1 dinner recipe with same structure  
    ],
    "snack": [
      // 1 snack recipe with same structure
    ]
  },
  "weeklySchedule": {
    "monday": {"breakfast": [0], "lunch": [0], "dinner": [0], "snack": [0]},
    "tuesday": {"breakfast": [], "lunch": [], "dinner": [], "snack": []},
    "wednesday": {"breakfast": [], "lunch": [], "dinner": [], "snack": []},
    "thursday": {"breakfast": [], "lunch": [], "dinner": [], "snack": []},
    "friday": {"breakfast": [], "lunch": [], "dinner": [], "snack": []},
    "saturday": {"breakfast": [], "lunch": [], "dinner": [], "snack": []},
    "sunday": {"breakfast": [], "lunch": [], "dinner": [], "snack": []}
  },
  "analysis": {
    "dailyTotals": {
      "p": ${dailyProteins},
      "c": ${dailyCarbs}, 
      "f": ${dailyFats},
      "kCals": ${dailyCalories}
    },
    "accuracy": "percentage_accuracy_to_macro_targets",
    "existingUsed": number_of_existing_recipes_incorporated,
    "newGenerated": number_of_new_recipes_created
  }
}

CRITICAL REQUIREMENTS:
- Return ONLY the JSON object above - no markdown, no code blocks, no explanatory text
- Do not wrap in \\\`\\\`\\\`json or \\\`\\\`\\\` blocks
- Do not add any text before or after the JSON
- Generate exactly 1 recipe per meal type (4 total recipes for Monday)
- Weekly schedule arrays contain recipe indexes - Monday uses [0] for each meal, other days are empty []
- Sum of ingredient macros must equal recipe total macros
- Use allowed units: g, mg, kg, ml, l, tsp, tbsp, lb, oz, floz, pinch, dash, drop, can, piece, scoop, fillet, item, cup, pt, gal, qt, clove
- SMART UNIT SELECTION: Use WEIGHT units (g, kg, oz, lb) for solids/dry ingredients (quinoa, rice, flour, nuts, cheese, pasta, etc.) for shopping practicality. Use VOLUME units (ml, l, floz, cup) for liquids only. Prefer grams for most solid ingredients.
- AVOID: volume units (cup, tbsp) for solid ingredients typically bought by weight
- For existing recipes, preserve their exact nutritional data
- Ensure dietary restriction compliance is 100%
- This creates a minimal starting point - users will add more recipes to other days and meals later`
    }
  ];
};

export const SingleRecipeForMealPlanMessage = ({ 
  mealType,
  dailyProteins, 
  dailyCarbs, 
  dailyFats, 
  dailyCalories,
  dietaryRestrictions, 
  customInstructions, 
  existingRecipes,
  excludeRecipeNames
}) => {
  const existingRecipesList = existingRecipes.filter(r => r.matchesRestrictions && !r.isArchived)
    .map(r => `- ${r.name} (${r.mealType}) - P:${r.proteins}g, C:${r.carbs}g, F:${r.fats}g, Fi:${r.fiber ?? 0}g, Cal:${r.kCals}, Servings:${r.servings}`)
    .join('\n');

  const excludeList = excludeRecipeNames && excludeRecipeNames.length > 0 
    ? excludeRecipeNames.join(', ') 
    : '';

  // Calculate target macros for this meal type based on meal distribution
  const getTargetMacros = (mealType) => {
    switch(mealType.toLowerCase()) {
      case 'breakfast':
        return {
          calories: Math.round(dailyCalories * 0.225), // 22.5% of daily calories
          protein: Math.round(dailyProteins * 0.225),
          carbs: Math.round(dailyCarbs * 0.225),
          fats: Math.round(dailyFats * 0.225)
        };
      case 'lunch':
        return {
          calories: Math.round(dailyCalories * 0.325), // 32.5% of daily calories
          protein: Math.round(dailyProteins * 0.325),
          carbs: Math.round(dailyCarbs * 0.325),
          fats: Math.round(dailyFats * 0.325)
        };
      case 'dinner':
        return {
          calories: Math.round(dailyCalories * 0.325), // 32.5% of daily calories
          protein: Math.round(dailyProteins * 0.325),
          carbs: Math.round(dailyCarbs * 0.325),
          fats: Math.round(dailyFats * 0.325)
        };
      case 'snack':
        return {
          calories: Math.round(dailyCalories * 0.125), // 12.5% of daily calories
          protein: Math.round(dailyProteins * 0.125),
          carbs: Math.round(dailyCarbs * 0.125),
          fats: Math.round(dailyFats * 0.125)
        };
      default:
        return {
          calories: Math.round(dailyCalories * 0.25),
          protein: Math.round(dailyProteins * 0.25),
          carbs: Math.round(dailyCarbs * 0.25),
          fats: Math.round(dailyFats * 0.25)
        };
    }
  };

  const targets = getTargetMacros(mealType);

  return [
    {
      role: "system",
      content: `You are a professional chef and registered dietitian with expertise in creating nutritionally precise recipes. You excel at:
      1. Calculating exact macronutrient values for ingredients and recipes
      2. Adapting recipes to meet specific dietary restrictions and allergies
      3. Balancing nutritional goals with culinary excellence
      4. Creating recipes that fit specific meal types and macro distributions
      5. Using ingredient substitutions to optimize both nutrition and taste
      6. Precise nutritional calculations for individual ingredients based on USDA data`
    },
    {
      role: "user",
      content: `Generate ONE ${mealType} recipe that fits into a weekly meal plan.

MEAL TYPE: ${mealType.toUpperCase()}
TARGET MACROS (for entire recipe):
- Calories: ${targets.calories} kcal
- Protein: ${targets.protein}g
- Carbohydrates: ${targets.carbs}g  
- Fats: ${targets.fats}g

DIETARY RESTRICTIONS: ${dietaryRestrictions}
${customInstructions ? `CUSTOM INSTRUCTIONS: "${customInstructions}"` : ''}

EXISTING RECIPES TO CONSIDER:
${existingRecipesList || 'No existing recipes match criteria'}

${excludeList ? `AVOID these recipes already in the meal plan: ${excludeList}` : ''}

REQUIREMENTS:
1. Create a single recipe that meets the target macros for ${mealType}
2. Must strictly comply with all dietary restrictions
3. Should be different from any excluded recipes
4. Include complete ingredient lists with individual macro breakdowns
5. Provide clear cooking instructions with timing
6. Focus on practical, delicious recipes suitable for ${mealType}`
    },
    {
      role: "user",
      content: `CRITICAL: You must respond with ONLY valid JSON. No explanations, no apologies, no markdown formatting, no code blocks.

If you cannot create a recipe, respond with: {"error": "Unable to create recipe with given constraints"}

Otherwise, respond with this exact JSON structure:

{
  "n": "Recipe name",
  "d": "Brief appetizing description (100-150 chars)",
  "ns": servings_as_number,
  "mac": {
    "p": total_protein_grams_for_entire_recipe,
    "c": total_carbs_grams_for_entire_recipe,
    "f": total_fats_grams_for_entire_recipe,
    "kCals": total_calories_for_entire_recipe
  },
  "ing": [
    {
      "n": "ingredient name",
      "q": quantity_as_number,
      "u": "unit",
      "mac": {
        "p": protein_grams_for_this_ingredient,
        "c": carbs_grams_for_this_ingredient,
        "f": fats_grams_for_this_ingredient,
        "kCals": calories_for_this_ingredient
      }
    }
  ],
  "i": [
    {
      "step": "cooking instruction",
      "duration": duration_in_seconds_or_null
    }
  ],
  "mealType": "${mealType}",
  "isExisting": false,
  "existingId": null,
  "dietaryRestrictions": [${dietaryRestrictions.split(', ').map(d => `"${d.trim()}"`).join(', ')}]
}

CRITICAL REQUIREMENTS:
- Your entire response must be ONLY valid JSON starting with { and ending with }
- NEVER include explanatory text like "Here's your recipe:" or "I'm sorry"
- NEVER wrap in \`\`\`json or \`\`\` blocks or any markdown formatting
- If you cannot create a recipe, return the error JSON format shown above
- Sum of ingredient macros must equal recipe total macros
- Use allowed units: g, mg, kg, ml, l, tsp, tbsp, lb, oz, floz, pinch, dash, drop, can, piece, scoop, fillet, item, cup, pt, gal, qt, clove
- SMART UNIT SELECTION: Use WEIGHT units (g, kg, oz, lb) for solids/dry ingredients (quinoa, rice, flour, nuts, cheese, pasta, etc.) for shopping practicality. Use VOLUME units (ml, l, floz, cup) for liquids only. Prefer grams for most solid ingredients.
- AVOID: volume units (cup, tbsp) for solid ingredients typically bought by weight
- Ensure dietary restriction compliance is 100%
- Target the specified macro goals for ${mealType}

Begin JSON response now:`
    }
  ];
};